import type { ThemeConfig } from "antd";
import { theme } from "antd/lib";
import deploymentLightOverrides from "./deployment-theme.light.json";
import deploymentDarkOverrides from "./deployment-theme.dark.json";

export interface TenantTheme {
    light?: ThemeConfig;
    dark?: ThemeConfig;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

// Recursively merges `override` on top of `base`. Plain-object branches are
// merged key-by-key; every other value (arrays, primitives, functions) is
// replaced wholesale by the override when present. `undefined` means "no
// override" and preserves the base value.
const deepMerge = <T,>(base: T, override: unknown): T => {
    if (override === undefined) return base;
    if (!isPlainObject(override)) return override as T;
    if (!isPlainObject(base)) return override as T;
    const result: Record<string, unknown> = { ...base };
    for (const key of Object.keys(override)) {
        result[key] = deepMerge(
            (base as Record<string, unknown>)[key],
            (override as Record<string, unknown>)[key],
        );
    }
    return result as T;
};

const baseLightThemeConfig: ThemeConfig = {
    token: {
        colorPrimaryText: "rgba(0, 0, 0, 0.85)",
        colorTextSecondary: "rgba(0, 0, 0, 0.65)",
        colorTextTertiary: "rgba(0, 0, 0, 0.45)",
        colorPrimary: "#f37233",
        colorBgContainer: "#f7f9fb",
        colorBgLayout: "#f7f9fb",
        colorBgElevated: "#ffffff",
        colorBorderBg: "#E8E9EA",
        borderRadius: 8,
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', 'Arial', 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
    },
    components: {
        Typography: {
            colorText: "rgba(0, 0, 0, 0.85)",
            colorTextDescription: "rgba(0, 0, 0, 0.65)",
            colorTextDisabled: "rgba(0, 0, 0, 0.45)",
        },
        Card: {
            colorBgContainer: "#FFFFFF",
            headerBg: "#FAFAFA",
            boxShadowTertiary:
                "0px 1px 2px 0px #00000008,0px 1px 6px -1px #000000050px,2px 4px 0px #00000005",
        },
        Table: {
            colorBgContainer: "#fff",
            headerBg: "transparent",
        },
        Input: {
            colorBgContainer: "#fff",
        },
        InputNumber: {
            colorBgContainer: "#fff",
        },
        Calendar: {
            colorBgContainer: "#FFFFFF",
        },
        Radio: {
            colorBgContainer: "#fff",
        },
        Select: {
            colorBgContainer: "#fff",
        },
    },
};

const baseDarkThemeConfig: ThemeConfig = {
    algorithm: theme.darkAlgorithm,
    token: {
        colorPrimaryText: "rgba(255, 255, 255, 0.85)",
        colorTextSecondary: "rgba(255, 255, 255, 0.65)",
        colorTextTertiary: "rgba(255, 255, 255, 0.45)",
        colorText: "rgba(255, 255, 255, 0.85)",
        colorTextDescription: "rgba(255, 255, 255, 0.65)",
        colorTextDisabled: "rgba(255, 255, 255, 0.45)",
        colorTextPlaceholder: "rgba(255, 255, 255, 0.45)",
        colorTextHeading: "rgba(255, 255, 255, 0.85)",
        colorPrimary: "#f37233",
        colorBgContainer: "#1f1f1f",
        colorBgLayout: "#141414",
        colorBgElevated: "#1f1f1f",
        colorBorderBg: "#303030",
        colorBorder: "#303030",
        colorBorderSecondary: "#303030",
        borderRadius: 8,
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', 'Arial', 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
    },
    components: {
        Typography: {
            colorText: "rgba(255, 255, 255, 0.85)",
            colorTextDescription: "rgba(255, 255, 255, 0.65)",
            colorTextDisabled: "rgba(255, 255, 255, 0.45)",
        },
        Card: {
            colorBgContainer: "#1f1f1f",
            headerBg: "#262626",
            boxShadowTertiary:
                "0px 1px 2px 0px #00000008,0px 1px 6px -1px #000000050px,2px 4px 0px #00000005",
        },
        Table: {
            colorBgContainer: "#1f1f1f",
            headerBg: "transparent",
            colorText: "rgba(255, 255, 255, 0.85)",
        },
        Input: {
            colorBgContainer: "#1f1f1f",
            colorText: "rgba(255, 255, 255, 0.85)",
            colorTextPlaceholder: "rgba(255, 255, 255, 0.45)",
            colorBorder: "#303030",
        },
        InputNumber: {
            colorBgContainer: "#1f1f1f",
            colorText: "rgba(255, 255, 255, 0.85)",
            colorBorder: "#303030",
        },
        Calendar: {
            colorBgContainer: "#1f1f1f",
            colorText: "rgba(255, 255, 255, 0.85)",
        },
        Radio: {
            colorBgContainer: "#1f1f1f",
            colorText: "rgba(255, 255, 255, 0.85)",
        },
        /*Select: {
            colorBgContainer: "#1f1f1f",
            colorText: "rgba(255, 255, 255, 0.85)",
            colorBorder: "#303030",
        },*/
        Button: {
            colorText: "rgba(255, 255, 255, 0.85)",
            colorBorder: "#303030",
        },
        Menu: {
            colorText: "rgba(255, 255, 255, 0.85)",
            colorBgContainer: "#1f1f1f",
            colorBorder: "#303030",
        },
        Layout: {
            colorBgContainer: "#141414",
        },
    },
};

// Merge per-deployment overrides (baked into the image at Docker build time)
// on top of the repo defaults. When no deployment override exists the JSON
// files are `{}` and these are equivalent to the base configs.
export const lightThemeConfig: ThemeConfig = deepMerge(
    baseLightThemeConfig,
    deploymentLightOverrides,
);
export const darkThemeConfig: ThemeConfig = deepMerge(
    baseDarkThemeConfig,
    deploymentDarkOverrides,
);

// Function to get theme based on mode
export const getThemeConfig = (
    mode: 'light' | 'dark',
    tenantTheme?: TenantTheme
): ThemeConfig => {
    // If tenant theme is provided and has a theme for the current mode, use it completely (complete override)
    if (tenantTheme ) {
        if (mode === 'light' && tenantTheme.light) {
            return tenantTheme.light;
        }
        if (mode === 'dark' && tenantTheme.dark) {
            return tenantTheme.dark;
        }
    }

    // Otherwise, fall back to the (deployment-merged) default theme
    return mode === 'light' ? lightThemeConfig : darkThemeConfig;
};

// Keep the original export for backward compatibility
export const themeConfig = lightThemeConfig;